import type { Incident, IncidentGroup } from '../../services/incidentsApi';
import { getIncidentPatternSelectedSignalCount } from './incidentPatternSemantics';

type PatternRecord = IncidentGroup | Incident | Record<string, any> | null | undefined;

function sourceRecord(value: PatternRecord): Record<string, any> | null {
  if (!value || typeof value !== 'object') return null;
  const raw = (value as any).raw;
  if (raw && typeof raw === 'object') return raw as Record<string, any>;
  return value as Record<string, any>;
}

function toCount(value: unknown): number {
  const count = Number(value ?? 0);
  return Number.isFinite(count) ? Math.max(0, count) : 0;
}

/** Read-side pattern volume (episode / lookback / queue rollup), not action write scope. */
export function getIncidentPatternReadVolumeCount(record: PatternRecord): number {
  const source = sourceRecord(record);
  if (!source) return 0;
  const lookback = toCount(
    source.pattern_source?.lookback_signal_count
    ?? source.recent_alert_count
    ?? source.risk_pattern?.recent_alert_count,
  );
  const windowSignal = getIncidentPatternWindowSignalCount(record);
  const alertCount = toCount(source.alert_count);
  return Math.max(lookback, windowSignal, alertCount);
}

/** Signals in the operator-selected review window (queue rollup / selected_window). */
export function getIncidentPatternWindowSignalCount(record: PatternRecord): number {
  return getIncidentPatternSelectedSignalCount(record);
}

export type PatternVolumeKind = 'persisted_episode' | 'review_window' | 'single';

export function resolvePatternVolumeKind(record: PatternRecord): PatternVolumeKind {
  const source = sourceRecord(record);
  if (!source) return 'single';
  const volume = getIncidentPatternReadVolumeCount(record);
  const window = getIncidentPatternWindowSignalCount(record);
  if (volume <= 1) return 'single';
  const lookback = toCount(
    source.pattern_source?.lookback_signal_count
    ?? source.recent_alert_count,
  );
  if (lookback > 0 && lookback > window) return 'persisted_episode';
  return 'review_window';
}

export function patternVolumeDiffersFromActionScope(
  patternVolume: number,
  actionScope: number | null | undefined,
): boolean {
  const volume = Math.max(0, Number(patternVolume) || 0);
  const scope = actionScope == null ? null : Math.max(0, Number(actionScope) || 0);
  if (volume <= 1 || scope == null) return false;
  return volume !== scope;
}
